import { CSSProperties, ClipboardEvent, KeyboardEvent, useRef } from 'react';

interface OtpInputProps {
  value: string[];
  onChange: (digits: string[]) => void;
  length?: number;
  disabled?: boolean;
  containerClassName?: string;
  boxClassName?: string;
  boxStyle?: CSSProperties;
}

export default function OtpInput({
  value,
  onChange,
  length = 6,
  disabled,
  containerClassName,
  boxClassName,
  boxStyle,
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  // Fill every box from a multi-digit string (autofill or paste), then
  // focus the last filled box. This is the path macOS/iOS code autofill
  // takes: the whole code lands in one input as a single change event.
  const distribute = (digits: string) => {
    const next = Array.from({ length }, (_, i) => digits[i] ?? '');
    onChange(next);
    const last = Math.min(digits.length, length) - 1;
    if (last >= 0) refs.current[last]?.focus();
  };

  const handleDigitChange = (index: number, raw: string) => {
    const digits = raw.replace(/\D/g, '');
    if (raw && !digits) return;                    // non-numeric input: ignore

    // A full code arrived in one event (autofill, or paste handled natively):
    // spread it across all boxes instead of cramming it into one.
    if (digits.length >= length) {
      distribute(digits);
      return;
    }

    // Normal single-character typing (typing over an existing digit gives
    // two chars — keep the newest one, as before).
    const next = [...value];
    next[index] = digits.slice(-1);
    onChange(next);
    if (digits && index < length - 1) {
      refs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const digits = e.clipboardData.getData('text').replace(/\D/g, '');
    if (digits.length > 1) {
      e.preventDefault();
      distribute(digits);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  return (
    <div className={containerClassName ?? 'flex gap-2 justify-center'}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => { refs.current[index] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          // Only the first box advertises itself to code autofill, and no
          // box carries maxLength — the full code must be allowed to land
          // in one field so distribute() can deal it out. (maxLength={1}
          // was the bug: macOS filled box one, five digits hit the floor.)
          autoComplete={index === 0 ? 'one-time-code' : 'off'}
          value={value[index] ?? ''}
          onChange={(e) => handleDigitChange(index, e.target.value)}
          onPaste={handlePaste}
          onKeyDown={(e) => handleKeyDown(index, e)}
          className={boxClassName}
          style={boxStyle}
          disabled={disabled}
        />
      ))}
    </div>
  );
}
